// frontend/src/components/handbook/HandbookMobileNav.js
import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { X, ChevronDown, ChevronRight, CheckCircle2 } from "lucide-react";
import { getChapterProgress } from "../../utils/handbookProgress";
import ContentTypeBadge from "./ContentTypeBadge";
import ChapterStatusBadge, { titleStyleForStatus } from "./ChapterStatusBadge";

const HandbookMobileNav = ({ handbook, isOpen, onClose, progressTick }) => {
  const { chapterSlug } = useParams();
  const [openSections, setOpenSections] = useState({});

  // Auto-open the section that contains the active chapter
  useEffect(() => {
    if (!isOpen || !handbook?.sections) return;
    const next = {};
    for (const section of handbook.sections) {
      const containsActive = section.chapters.some(
        (c) => c.slug === chapterSlug
      );
      next[section.id] = containsActive;
    }
    setOpenSections(next);
  }, [isOpen, chapterSlug, handbook]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!handbook?.sections) return null;

  const toggleSection = (id) => {
    setOpenSections((s) => ({ ...s, [id]: !s[id] }));
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity md:hidden ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Bottom sheet */}
      <div
        className={`fixed left-0 right-0 bottom-0 z-50 bg-white rounded-t-2xl shadow-2xl md:hidden transition-transform duration-300 ${
          isOpen ? "translate-y-0" : "translate-y-full"
        }`}
        style={{ maxHeight: "85vh" }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-2 pb-1">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
          <h2 className="text-base font-semibold text-[#002855]">
            {handbook.title || "Coach Handbook"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schliessen"
            className="p-2 -mr-2 rounded-md hover:bg-gray-100 text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Sections */}
        <div className="overflow-y-auto" style={{ maxHeight: "calc(85vh - 80px)" }}>
          <ul className="divide-y divide-gray-100">
            {handbook.sections.map((section) => {
              const isOpen = !!openSections[section.id];
              return (
                <li key={section.id}>
                  <button
                    type="button"
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-2">
                      {isOpen ? (
                        <ChevronDown className="w-4 h-4 text-[#00A3E0]" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-[#00A3E0]" />
                      )}
                      <span className="font-semibold text-[#002855]">
                        {section.title}
                      </span>
                    </div>
                    <span className="text-xs text-gray-400">
                      {section.chapters.length}
                    </span>
                  </button>

                  {isOpen && (
                    <ul className="pb-2">
                      {section.chapters.map((chapter) => {
                        const isActive = chapter.slug === chapterSlug;
                        const { done, total } = getChapterProgress(
                          chapter.slug,
                          chapter.checklists
                        );
                        const allDone = total > 0 && done === total;
                        return (
                          <li key={chapter.slug}>
                            <Link
                              to={`/coach-handbook/${chapter.slug}`}
                              onClick={onClose}
                              className={`flex items-center gap-2 pl-10 pr-4 py-3 text-sm ${
                                isActive
                                  ? "bg-[#002855] text-white"
                                  : "text-gray-700 active:bg-gray-100"
                              }`}
                            >
                              <ContentTypeBadge
                                type={chapter.content_type}
                                compact
                                className="flex-shrink-0"
                              />
                              <span
                                className={`flex-1 truncate ${titleStyleForStatus(
                                  chapter.status
                                )}`}
                              >
                                {chapter.title}
                              </span>
                              <ChapterStatusBadge
                                status={chapter.status}
                                compact
                                className="flex-shrink-0"
                              />
                              {total > 0 && (
                                <span
                                  className={`flex-shrink-0 text-xs flex items-center gap-1 ${
                                    isActive
                                      ? "text-white/80"
                                      : allDone
                                      ? "text-[#84BD00]"
                                      : "text-gray-400"
                                  }`}
                                >
                                  {allDone ? (
                                    <CheckCircle2 className="w-4 h-4" />
                                  ) : (
                                    `${done}/${total}`
                                  )}
                                </span>
                              )}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </>
  );
};

export default HandbookMobileNav;
